/**
 * Automatic content translation layer.
 *
 * All content (news, programs, videos, adverts, etc.) is authored in English.
 * When a visitor switches to French, we translate the raw text on demand using
 * a FREE, key-less translation endpoint (no API key required), then cache the
 * result so repeat visitors never re-translate the same string.
 *
 * Two cache layers:
 *  1. In-memory Map — instant for the current browsing session.
 *  2. IndexedDB — persistent across sessions/browser restarts.
 */

const memoryCache = new Map<string, string>();

const DB_NAME = "ltv-translations";
const STORE = "cache";

let dbPromise: Promise<IDBDatabase> | null = null;

function getDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      try {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => {
          if (!req.result.objectStoreNames.contains(STORE)) {
            req.result.createObjectStore(STORE);
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      } catch (e) {
        reject(e);
      }
    });
  }
  return dbPromise;
}

function idbGet(key: string): Promise<string | undefined> {
  return getDb()
    .then(
      (db) =>
        new Promise<string | undefined>((resolve) => {
          try {
            const tx = db.transaction(STORE, "readonly");
            const req = tx.objectStore(STORE).get(key);
            req.onsuccess = () => resolve(typeof req.result === "string" ? req.result : undefined);
            req.onerror = () => resolve(undefined);
          } catch {
            resolve(undefined);
          }
        }),
    )
    .catch(() => undefined);
}

function idbSet(key: string, value: string): void {
  getDb()
    .then((db) => {
      try {
        const tx = db.transaction(STORE, "readwrite");
        tx.objectStore(STORE).put(value, key);
      } catch {
        /* ignore */
      }
    })
    .catch(() => {
      /* ignore */
    });
}

function cacheKey(text: string, lang: string): string {
  return `${lang}::${text}`;
}

/**
 * Free key-less translation. Tries Google's public translate endpoint first
 * (no key needed), then falls back to MyMemory (also free). Returns the
 * original text if both fail, so the page never breaks.
 */
async function translateOne(text: string, target: string): Promise<string> {
  // Attempt 1 — Google public gtx endpoint (browser-compatible, CORS enabled).
  try {
    const url =
      "https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=" +
      encodeURIComponent(target) +
      "&dt=t&q=" +
      encodeURIComponent(text);
    const res = await fetch(url);
    if (res.ok) {
      const data = await res.json();
      const segments = Array.isArray(data) && Array.isArray(data[0]) ? data[0] : [];
      const out = segments
        .map((seg: unknown) =>
          Array.isArray(seg) && typeof seg[0] === "string" ? (seg[0] as string) : "",
        )
        .join("");
      if (out) return out;
    }
  } catch {
    /* fall through to next provider */
  }

  // Attempt 2 — MyMemory free API (no key required).
  try {
    const url =
      "https://api.mymemory.translated.net/get?q=" +
      encodeURIComponent(text) +
      "&langpair=en|" +
      encodeURIComponent(target);
    const res = await fetch(url);
    if (res.ok) {
      const data = await res.json();
      const translated = data?.responseData?.translatedText;
      if (typeof translated === "string" && translated) return translated;
    }
  } catch {
    /* give up */
  }

  return text;
}

/**
 * Translate an array of strings into `targetLang` (e.g. "fr").
 * Returns a same-length array. Untranslatable/empty strings pass through
 * unchanged, and any network error leaves the original text intact.
 */
export async function translateBatch(texts: string[], targetLang: string): Promise<string[]> {
  if (!texts.length || targetLang === "en") return texts;

  const cleaned = texts.map((s) => (typeof s === "string" ? s.trim() : ""));

  const result: string[] = new Array(cleaned.length).fill("");
  const pendingIndexes: number[] = [];
  const missing: string[] = [];

  for (let i = 0; i < cleaned.length; i++) {
    const text = cleaned[i];
    if (!text) {
      result[i] = text;
      continue;
    }
    const key = cacheKey(text, targetLang);
    const cached = memoryCache.get(key);
    if (cached != null) {
      result[i] = cached;
      continue;
    }
    result[i] = text;
    pendingIndexes.push(i);
    missing.push(text);
  }

  if (pendingIndexes.length === 0) return result;

  // Resolve persistent (IndexedDB) cache first, then translate only the remainder.
  const idxByText = new Map<string, number[]>();
  pendingIndexes.forEach((idx) => {
    const key = cacheKey(cleaned[idx], targetLang);
    const arr = idxByText.get(key) || [];
    arr.push(idx);
    idxByText.set(key, arr);
  });

  const toFetch: string[] = [];
  const fetchIndexes: number[] = [];

  await Promise.all(
    [...idxByText.entries()].map(async ([key, indexes]) => {
      const persisted = await idbGet(key);
      if (persisted != null) {
        memoryCache.set(key, persisted);
        indexes.forEach((idx) => {
          result[idx] = persisted;
        });
      } else {
        indexes.forEach((idx) => {
          toFetch.push(cleaned[idx]);
          fetchIndexes.push(idx);
        });
      }
    }),
  );

  if (toFetch.length === 0) return result;

  // Translate the missing strings with a small concurrency pool to stay fast
  // without hammering the free endpoints.
  const CONCURRENCY = 6;
  const translations: string[] = new Array(toFetch.length).fill("");
  let cursor = 0;

  const workers = Array.from({ length: Math.min(CONCURRENCY, toFetch.length) }, async () => {
    while (cursor < toFetch.length) {
      const idx = cursor++;
      if (idx >= toFetch.length) break;
      try {
        translations[idx] = await translateOne(toFetch[idx], targetLang);
      } catch {
        translations[idx] = toFetch[idx];
      }
    }
  });

  await Promise.all(workers);

  fetchIndexes.forEach((idx, j) => {
    const translated = translations[j];
    const final = typeof translated === "string" && translated ? translated : cleaned[idx];
    result[idx] = final;
    const key = cacheKey(cleaned[idx], targetLang);
    memoryCache.set(key, final);
    idbSet(key, final);
  });

  return result;
}